import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { supabase } from "../supabaseClient";
import { useEffect } from "react";
import { Lock, CreditCard } from 'lucide-react';

const Checkout = () => {
  const { cart, cartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const API_URL = 'https://i-kept-all-the-code-in-github-and-now-i.onrender.com';
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    phone: ''
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const shippingCharge = 100;
const finalTotal = cartTotal + shippingCharge;

  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      });
    };
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        const meta = data.user.user_metadata || {};
        setFormData((prev) => ({
          ...prev,
          email: data.user.email || "",
          firstName: meta.full_name?.split(" ")[0] || "",
          lastName: meta.full_name?.split(" ")[1] || "",
          phone: meta.phone || "",
        }));
      }
    };
    loadUser();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const createRazorpayOrder = async (orderId, amount) => {
    try {
      const response = await fetch(`${API_URL}/api/create-razorpay-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId, amount, currency: 'INR' }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Server error: ${response.status}`);
      }
      return data;
    } catch (error) {
      console.error('Error creating Razorpay order:', error);
      throw error;
    }
  };

  const verifyPayment = async (paymentResponse, dbOrderId) => {
    try {
      const verificationResponse = await fetch(`${API_URL}/api/verify-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          order_id: paymentResponse.razorpay_order_id,
          payment_id: paymentResponse.razorpay_payment_id,
          signature: paymentResponse.razorpay_signature,
          db_order_id: dbOrderId,
        }),
      });

      const result = await verificationResponse.json();

      if (!verificationResponse.ok || !result.success) {
        throw new Error(result.error || 'Verification failed');
      }

      // Backend already updates status to 'paid' — no duplicate update needed
      clearCart();
      navigate(`/thank-you?orderId=${dbOrderId}`);

    } catch (error) {
      console.error('Payment verification error:', error.message);
      alert("Payment verification failed. Please contact support.");
      setIsProcessing(false);
    }
  };

  const openRazorpay = (razorpayOrder, dbOrderId) => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency,
      name: 'Siyaanish',
      description: `Order #${dbOrderId}`,
      order_id: razorpayOrder.id,
      prefill: {
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        contact: formData.phone,
      },
      notes: {
        address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`,
        order_id: dbOrderId,
      },
      theme: { color: '#ba8245' },
      handler: async (response) => {
        await verifyPayment(response, dbOrderId);
      },
      modal: {
        ondismiss: () => setIsProcessing(false),
      },
    };
    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

const handleSubmit = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      alert("Your cart is empty. Please add items before checkout.");
      navigate('/cart');
      return;
    }

    if (finalTotal <= 0) {
      alert("Invalid order amount. Please check your cart.");
      return;
    }

    setIsProcessing(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user ? user.id : null;

      // ✅ Build items array from cart
      const items = cart.map((item) => ({
        product_id: item.id,
        product_name: item.name,
        product_image: item.images[0],
        size: item.size,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.price * item.quantity,
      }));

      // ✅ Save everything in one orders insert — no order_items needed
      const { data: orderData, error: orderError } = await supabase
        .from("orders")
        .insert([{
          user_id: userId,
          total_amount: finalTotal,
          status: "pending",
          payment_method: "razorpay",
          customer_name: `${formData.firstName} ${formData.lastName}`,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          email: formData.email,
          phone: formData.phone,
          items: items,           // ✅ All product details saved here as JSON
        }])
        .select()
        .single();

      if (orderError) throw orderError;

      const orderId = orderData.id;

      // ✅ Create Razorpay order & open checkout
      const razorpayOrderData = await createRazorpayOrder(orderId, finalTotal);
      openRazorpay(razorpayOrderData, orderId);

    } catch (err) {
      console.error(err);
      alert("Order failed. Try again.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-serif text-4xl md:text-5xl text-charcoal mb-12" data-testid="checkout-page-title">
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Checkout Form */}
          <div>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h2 className="font-serif text-2xl text-charcoal mb-6">Contact Information</h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                      data-testid="checkout-email-input"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-charcoal mb-6">Shipping Address</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-firstname-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-lastname-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                      data-testid="checkout-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">City</label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-city-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-state-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-pincode-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-charcoal mb-2 block">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-[#E5E0D8] px-0 py-3 focus:border-bronze focus:outline-none"
                        data-testid="checkout-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-charcoal mb-6">Payment Method</h2>
                <div className="bg-white p-6 border border-[#E5E0D8]">
                  <div className="flex items-center gap-3 mb-4">
                    <CreditCard className="text-bronze" size={24} />
                    <span className="text-charcoal font-medium">Razorpay (Cards, UPI, Wallets)</span>
                  </div>
                  <p className="text-sm text-[#6B665F] font-light">
                    Secure payment processing via Razorpay. All major cards, UPI, and wallets accepted.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isProcessing}
                className={`w-full bg-[#ba8245] text-white px-8 py-4 rounded-lg shadow-md hover:bg-[#a8733d] hover:shadow-lg transition-all duration-300 text-sm tracking-wide font-medium flex items-center justify-center gap-2 ${
                  isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                data-testid="place-order-button"
              >
                <Lock size={18} />
                {isProcessing ? 'Processing...' : 'Place Order'}
              </button>

              <p className="text-xs text-center text-[#6B665F] font-light">
                Your payment information is secure and encrypted
              </p>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white p-8 border border-[#E5E0D8] sticky top-24">
              <h2 className="font-serif text-2xl text-charcoal mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6" data-testid="checkout-order-items">
                {cart.map((item) => (
                  <div key={`${item.id}-${item.size}`} className="flex gap-4">
                    <img src={item.images[0]} alt={item.name} className="w-16 h-20 object-cover" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-charcoal">{item.name}</h4>
                      <p className="text-xs text-[#6B665F]">Size: {item.size} | Qty: {item.quantity}</p>
                      <p className="text-sm text-bronze font-medium mt-1">
                        ₹{(item.price * item.quantity).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="h-px bg-[#E5E0D8] my-6"></div>

              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B665F]">Subtotal</span>
                  <span className="text-charcoal font-medium">₹{cartTotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#6B665F]">Shipping</span>
                    <span className="text-charcoal font-medium">₹100</span>
                    
                </div>
              </div>

              <div className="h-px bg-[#E5E0D8] my-6"></div>

              <div className="flex justify-between">
                <span className="font-serif text-lg text-charcoal">Total</span>
                <span className="font-serif text-xl text-bronze" data-testid="checkout-total">
                  ₹{finalTotal.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;